import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001'}/api`;

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (token) {
      checkToken();
    } else {
      setError('Invalid invitation link');
      setChecking(false);
    }
  }, [token]);

  const checkToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/check-invite?token=${token}`);
      setUserInfo(response.data?.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired invitation link');
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/set-password`, { token, password });
      setSuccess(true);
      toast.success('Password set successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
        <AppFooter noBorder />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-background" data-testid="set-password-error">
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md border-destructive">
            <CardHeader>
              <CardTitle className="text-xl text-destructive">INVALID LINK</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/login')} className="w-full">
                GO TO LOGIN
              </Button>
            </CardContent>
          </Card>
        </div>
        <AppFooter noBorder />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-background" data-testid="set-password-success">
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md border-green-500">
            <CardHeader className="text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-xl">PASSWORD SET!</CardTitle>
              <CardDescription>Redirecting to login...</CardDescription>
            </CardHeader>
          </Card>
        </div>
        <AppFooter noBorder />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="set-password-page">
      <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl tracking-tight uppercase text-foreground">
            SET PASSWORD
          </h1>
          <p className="mt-2 text-muted-foreground text-sm">
            Create a password for your account
          </p>
        </div>

        <Card className="border-border bg-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">WELCOME, {userInfo?.name?.toUpperCase()}</CardTitle>
            <CardDescription>{userInfo?.email}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">PASSWORD</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pr-10"
                    data-testid="set-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">CONFIRM PASSWORD</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pr-10"
                    data-testid="set-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-destructive">Passwords don't match</p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading || password !== confirmPassword}
                data-testid="set-password-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'SET PASSWORD & ACTIVATE'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      </div>
      <AppFooter noBorder />
    </div>
  );
};

export default SetPasswordPage;
